import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import {
  ArrowRightFill,
  CheckmarkCircleFill,
  CheckmarkFill,
  Close,
  PauseOutline,
  PlayFill,
  RadioButtonOffOutline,
  SettingsOutline,
} from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Button, IconButton, Loader, Popover, Select, Stack, Typography } from '@lyearn/ui';
import { getSelectOptionLabel } from '@/components/Form/components/Select/getOptionLabel';
import { Image } from '@/components/Image';
import { VideoCharacterProviderEnum } from '@/types/schema';

import AvatarPreviewModal from '../AvatarPreviewModal';
import AvatarGridView from '../AvatarSelectionModal';
import { useVideoCharacterAvatars } from '../useVideoCharacterAvatars';
import { useVideoCharacterVoices } from '../useVideoCharacterVoices';
import { getDefaultVoice, getSelectValue, getVoiceOptions } from '../utils/voiceOptions';

export type VideoSettingsMode = 'voiceOver' | 'avatarNarration';

interface VideoSettingsPopoverProps {
  videoType?: VideoSettingsMode;
  avatarId: string;
  voiceId: string;
  onApplySettings: (config: {
    avatarId: string;
    voiceId: string;
    videoType: VideoSettingsMode;
  }) => void;
}

const DEFAULT_VIDEO_SETTINGS_MODE: VideoSettingsMode = 'avatarNarration';
const SHOW_VOICE_OVER_OPTION = false;
const VISIBLE_AVATAR_COUNT = 5;

const getResolvedVideoSettingsMode = (videoType?: string): VideoSettingsMode => {
  return videoType === 'voiceOver' ? 'voiceOver' : DEFAULT_VIDEO_SETTINGS_MODE;
};

const getVoiceOnlyDefaultVoice = ({
  voices,
  preferredVoiceId,
}: {
  voices: ReturnType<typeof useVideoCharacterVoices>['voices'];
  preferredVoiceId?: string;
}) => {
  if (preferredVoiceId && voices.some((voice) => voice.voice_id === preferredVoiceId)) {
    return preferredVoiceId;
  }

  return voices[0]?.voice_id || '';
};

const getCreatedByName = (voice: ReturnType<typeof useVideoCharacterVoices>['voices'][number]) => {
  const createdBy = voice?.createdBy;
  return createdBy ? `${createdBy.firstName ?? ''} ${createdBy.lastName ?? ''}`.trim() : '';
};

export function VideoSettingsPopover({
  videoType,
  avatarId,
  voiceId,
  onApplySettings,
}: VideoSettingsPopoverProps) {
  const { t } = useTranslation('Course');
  const [isOpen, setIsOpen] = React.useState(false);
  const [localVideoType, setLocalVideoType] = React.useState<VideoSettingsMode>(
    getResolvedVideoSettingsMode(videoType),
  );
  const [localAvatarId, setLocalAvatarId] = React.useState(avatarId);
  const [localVoiceId, setLocalVoiceId] = React.useState(voiceId);
  const [showAvatarGrid, setShowAvatarGrid] = React.useState(false);
  const [previewAvatarId, setPreviewAvatarId] = React.useState<string | null>(null);
  const [playingVoiceId, setPlayingVoiceId] = React.useState<string | null>(null);
  const voiceAudioRef = React.useRef<HTMLAudioElement | null>(null);

  const {
    avatars,
    fetching: avatarsFetching,
    defaultAvatarValue,
  } = useVideoCharacterAvatars({ filters: { providers: [VideoCharacterProviderEnum.Heygen] } });
  const { voices, fetching: voicesFetching } = useVideoCharacterVoices({
    filters: { providers: [VideoCharacterProviderEnum.Heygen] },
  });

  const selectedAvatar = React.useMemo(
    () =>
      avatars.find((avatar) => avatar.avatar_id === localAvatarId) ||
      avatars.find((avatar) => avatar.avatar_id === defaultAvatarValue) ||
      null,
    [avatars, defaultAvatarValue, localAvatarId],
  );
  const previewAvatar = previewAvatarId
    ? avatars.find((avatar) => avatar.avatar_id === previewAvatarId) ?? null
    : null;
  const selectedAvatarGender = selectedAvatar?.gender ?? null;

  const filteredVoices = React.useMemo(() => {
    if (localVideoType === 'voiceOver') {
      return voices;
    }

    const genderVoices = getVoiceOptions({
      voices,
      genderValue: selectedAvatarGender,
    });

    return genderVoices.length > 0 ? genderVoices : voices;
  }, [localVideoType, selectedAvatarGender, voices]);

  const voiceOptions = React.useMemo(
    () =>
      filteredVoices.map((voice) => ({
        label: voice.name,
        value: voice.voice_id,
        voice,
      })),
    [filteredVoices],
  );

  const resolvedVoiceId = React.useMemo(() => {
    if (localVideoType === 'voiceOver') {
      return getVoiceOnlyDefaultVoice({
        voices,
        preferredVoiceId: localVoiceId,
      });
    }

    if (filteredVoices.some((voice) => voice.voice_id === localVoiceId)) {
      return localVoiceId;
    }

    return getDefaultVoice({
      voices,
      genderValue: selectedAvatarGender,
      preferredVoiceValue: (localVoiceId || selectedAvatar?.default_voice_id) ?? undefined,
    });
  }, [
    filteredVoices,
    localVideoType,
    localVoiceId,
    selectedAvatar?.default_voice_id,
    selectedAvatarGender,
    voices,
  ]);

  const visibleAvatars = React.useMemo(() => {
    const firstVisibleAvatars = avatars.slice(0, VISIBLE_AVATAR_COUNT);
    const selectedAvatarInView = firstVisibleAvatars.some(
      (avatar) => avatar.avatar_id === selectedAvatar?.avatar_id,
    );

    return !selectedAvatarInView && selectedAvatar
      ? [selectedAvatar, ...firstVisibleAvatars.slice(0, VISIBLE_AVATAR_COUNT - 1)]
      : firstVisibleAvatars;
  }, [avatars, selectedAvatar]);

  const isApplyDisabled =
    !resolvedVoiceId || (localVideoType === 'avatarNarration' && !selectedAvatar?.avatar_id);

  React.useEffect(() => {
    if (localVoiceId !== resolvedVoiceId) {
      setLocalVoiceId(resolvedVoiceId);
    }
  }, [localVoiceId, resolvedVoiceId]);

  const handleAvatarChange = React.useCallback(
    (newAvatarId: string) => {
      setLocalAvatarId(newAvatarId);
      const nextAvatar = avatars.find((avatar) => avatar.avatar_id === newAvatarId);
      setLocalVoiceId(
        getDefaultVoice({
          voices,
          genderValue: nextAvatar?.gender ?? null,
          preferredVoiceValue: newAvatarId ? nextAvatar?.default_voice_id ?? undefined : undefined,
        }),
      );
    },
    [avatars, voices],
  );

  const handleAvatarSelectedFromGrid = React.useCallback(
    (newAvatarId: string) => {
      handleAvatarChange(newAvatarId);
      setShowAvatarGrid(false);
    },
    [handleAvatarChange],
  );

  const stopVoicePreview = React.useCallback(() => {
    voiceAudioRef.current?.pause();
    voiceAudioRef.current = null;
    setPlayingVoiceId(null);
  }, []);

  React.useEffect(() => {
    return () => {
      stopVoicePreview();
    };
  }, [stopVoicePreview]);

  const handleOpen = React.useCallback(() => {
    const nextVideoType = getResolvedVideoSettingsMode(videoType);
    const nextAvatarId = avatarId || defaultAvatarValue;
    const nextAvatar =
      avatars.find((avatar) => avatar.avatar_id === nextAvatarId) ||
      avatars.find((avatar) => avatar.avatar_id === defaultAvatarValue) ||
      null;
    const nextVoiceId =
      nextVideoType === 'voiceOver'
        ? getVoiceOnlyDefaultVoice({
            voices,
            preferredVoiceId: voiceId,
          })
        : getDefaultVoice({
            voices,
            genderValue: nextAvatar?.gender ?? null,
            preferredVoiceValue: (voiceId || nextAvatar?.default_voice_id) ?? undefined,
          });

    setLocalVideoType(nextVideoType);
    setLocalAvatarId(nextAvatarId);
    setLocalVoiceId(nextVoiceId);
    setShowAvatarGrid(false);
    setPreviewAvatarId(null);
    setIsOpen(true);
  }, [avatarId, avatars, defaultAvatarValue, videoType, voiceId, voices]);

  const handleVoicePreviewClick = React.useCallback(
    (e: React.MouseEvent, voiceIdValue: string, previewUrl?: string | null) => {
      e.stopPropagation();
      e.preventDefault();
      if (!previewUrl) {
        return;
      }

      if (playingVoiceId === voiceIdValue) {
        stopVoicePreview();
        return;
      }

      voiceAudioRef.current?.pause();
      const nextAudio = new Audio(previewUrl);
      voiceAudioRef.current = nextAudio;
      nextAudio.play();
      setPlayingVoiceId(voiceIdValue);
      nextAudio.onended = () => setPlayingVoiceId(null);
    },
    [playingVoiceId, stopVoicePreview],
  );

  const handleClose = React.useCallback(() => {
    stopVoicePreview();
    setIsOpen(false);
    setShowAvatarGrid(false);
    setPreviewAvatarId(null);
  }, [stopVoicePreview]);

  const handleApply = React.useCallback(() => {
    stopVoicePreview();
    onApplySettings({
      avatarId: localVideoType === 'voiceOver' ? '' : selectedAvatar?.avatar_id || '',
      voiceId: resolvedVoiceId,
      videoType: localVideoType,
    });
    setIsOpen(false);
    setShowAvatarGrid(false);
    setPreviewAvatarId(null);
  }, [
    localVideoType,
    onApplySettings,
    resolvedVoiceId,
    selectedAvatar?.avatar_id,
    stopVoicePreview,
  ]);

  const handleVideoTypeChange = React.useCallback(
    (nextVideoType: VideoSettingsMode) => {
      setLocalVideoType(nextVideoType);
      if (nextVideoType === 'voiceOver') {
        setShowAvatarGrid(false);
        return;
      }

      if (!localAvatarId && defaultAvatarValue) {
        setLocalAvatarId(defaultAvatarValue);
      }
    },
    [defaultAvatarValue, localAvatarId],
  );

  const videoTypeModes = React.useMemo<Array<{ id: VideoSettingsMode; label: string }>>(() => {
    const modes: Array<{ id: VideoSettingsMode; label: string }> = [
      { id: 'avatarNarration', label: t('Course:AiVideoGeneration.AvatarAndVoice') },
    ];
    if (SHOW_VOICE_OVER_OPTION) {
      modes.unshift({ id: 'voiceOver', label: t('Course:AiVideoGeneration.VoiceOver') });
    }
    return modes;
  }, [t]);

  const renderVideoTypeSelector = (
    <Stack direction="row" gap={16}>
      {videoTypeModes.map((mode) => {
        const isSelected = localVideoType === mode.id;

        return (
          <Stack
            key={mode.id}
            className={cn(
              'flex-1 cursor-pointer rounded-[8px] border px-16 py-12',
              isSelected ? 'border-text-primary' : 'border-border',
            )}
            direction="col"
            role="button"
            tabIndex={0}
            onClick={() => handleVideoTypeChange(mode.id)}
            onKeyDown={(e: React.KeyboardEvent) =>
              e.key === 'Enter' && handleVideoTypeChange(mode.id)
            }>
            <Stack alignItems="center" direction="row" justifyContent="between">
              <Typography className="font-medium" color="text-text-primary" variant="body-short-01">
                {mode.label}
              </Typography>
              {isSelected ? (
                <CheckmarkCircleFill height={24} width={24} />
              ) : (
                <RadioButtonOffOutline className="text-icon-placeholder" height={20} width={20} />
              )}
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );

  return (
    <>
      <Popover open={isOpen} onOpenChange={(next) => (next ? handleOpen() : handleClose())}>
        <Popover.Button>
          <Button
            StartIcon={SettingsOutline}
            appearance="secondary"
            shape="square"
            size="small"
            variant="outlined">
            {t('Course:AiVideoGeneration.VideoSettings')}
          </Button>
        </Popover.Button>
        <Popover.Content
          className={cn(
            'flex max-h-[72rem] flex-col overflow-hidden rounded-20 bg-bg-primary p-0 shadow-lg',
            showAvatarGrid ? 'w-[58rem]' : 'w-[46rem]',
          )}
          position="bottom-end"
          renderAs="popover">
          {showAvatarGrid ? (
            <Stack className="min-h-0 flex-1 overflow-y-auto p-20" direction="col">
              <AvatarGridView
                avatars={avatars}
                isLoading={avatarsFetching}
                selectedAvatarId={selectedAvatar?.avatar_id || ''}
                onBack={() => setShowAvatarGrid(false)}
                onPreviewAvatar={setPreviewAvatarId}
                onSelectAvatar={handleAvatarSelectedFromGrid}
              />
            </Stack>
          ) : (
            <>
              <Stack
                alignItems="center"
                className="shrink-0 px-20 pt-20 pb-16"
                direction="row"
                justifyContent="between">
                <Typography color="text-text-primary" variant="heading-xs">
                  {t('Course:AiVideoGeneration.VideoSettings')}
                </Typography>
                <Stack
                  alignItems="center"
                  aria-label={t('Common:Actions.Close')}
                  className="cursor-pointer rounded-[1.6rem] bg-bg-primary p-12"
                  justifyContent="center"
                  onClick={handleClose}>
                  <Close className="h-24 w-24 text-text-primary" />
                </Stack>
              </Stack>
              <Stack
                className="min-h-0 flex-1 overflow-y-auto px-20 pb-20"
                direction="col"
                gap={28}>
                {renderVideoTypeSelector}
                {localVideoType === 'avatarNarration' ? (
                  <>
                    <Stack direction="col" gap={8}>
                      <Typography color="text-text-primary" variant="body-short-02">
                        {t('Course:AiVideoGeneration.Preview')}
                      </Typography>
                      <Stack
                        alignItems="center"
                        className="relative w-full overflow-hidden rounded-12 border border-border bg-[#000000]"
                        direction="col">
                        <Stack
                          alignItems="center"
                          className="w-full overflow-hidden"
                          justifyContent="center"
                          style={{ aspectRatio: '16/9' }}>
                          {selectedAvatar?.preview_video_url ? (
                            <video
                              key={selectedAvatar.avatar_id}
                              controls
                              playsInline
                              className="h-full w-full object-cover"
                              src={selectedAvatar.preview_video_url}
                            />
                          ) : selectedAvatar?.preview_image_url ? (
                            <Image
                              alt={selectedAvatar.avatar_name}
                              className="h-full w-full object-cover"
                              height={225}
                              src={selectedAvatar.preview_image_url}
                              width={400}
                            />
                          ) : null}
                        </Stack>
                      </Stack>
                    </Stack>

                    <Stack direction="col" gap={12}>
                      <Stack alignItems="center" direction="row" justifyContent="between">
                        <Typography
                          className="font-medium"
                          color="text-text-primary"
                          variant="body-short-01">
                          {t('Course:AiVideoGeneration.Avatar')}
                        </Typography>
                        {avatars.length > VISIBLE_AVATAR_COUNT ? (
                          <Button
                            EndIcon={ArrowRightFill}
                            size="x-small"
                            variant="ghost"
                            onClick={() => setShowAvatarGrid(true)}>
                            {t('Common:Actions.SeeAll')}
                          </Button>
                        ) : null}
                      </Stack>
                      {avatarsFetching ? (
                        <Loader size={16} />
                      ) : (
                        <Stack className="flex-nowrap gap-12 overflow-hidden" direction="row">
                          {visibleAvatars.map((avatar) => {
                            const isSelected = selectedAvatar?.avatar_id === avatar.avatar_id;

                            return (
                              <Stack
                                key={avatar.avatar_id}
                                className={cn(
                                  'relative shrink-0 cursor-pointer overflow-hidden rounded-12 border',
                                  isSelected ? 'border-2 border-text-primary' : 'border-border',
                                )}
                                style={{ width: 72, height: 74 }}
                                onClick={() => handleAvatarChange(avatar.avatar_id)}>
                                <Image
                                  alt={avatar.avatar_name}
                                  className="h-full w-full object-cover object-top"
                                  height={74}
                                  src={avatar.preview_image_url || ''}
                                  width={72}
                                />
                                {isSelected ? (
                                  <span className="absolute right-4 top-4 flex h-24 w-24 items-center justify-center rounded-full bg-bg-primary">
                                    <CheckmarkCircleFill height={24} width={24} />
                                  </span>
                                ) : null}
                              </Stack>
                            );
                          })}
                        </Stack>
                      )}
                    </Stack>

                    <Stack direction="col" gap={12}>
                      <Typography
                        className="font-medium"
                        color="text-text-primary"
                        variant="body-short-01">
                        {t('Course:AiVideoGeneration.Voice')}
                      </Typography>
                      {voicesFetching ? (
                        <Loader size={16} />
                      ) : (
                        <Select
                          disableClearable
                          disabled={voiceOptions.length === 0}
                          getOptionLabel={getSelectOptionLabel}
                          options={voiceOptions}
                          renderOption={(option) => (
                            <Stack
                              alignItems="center"
                              className="w-full gap-12 py-4"
                              direction="row">
                              <IconButton
                                appearance="secondary"
                                aria-label={
                                  playingVoiceId === option.value ? 'Pause preview' : 'Play preview'
                                }
                                className="h-40 w-40 shrink-0 rounded-full border border-divider bg-bg-primary"
                                disabled={!option.voice?.preview_audio}
                                shape="square"
                                size="xs"
                                variant="ghost"
                                onClick={(e) =>
                                  handleVoicePreviewClick(
                                    e,
                                    option.value,
                                    option.voice?.preview_audio,
                                  )
                                }>
                                {playingVoiceId === option.value ? (
                                  <PauseOutline className="h-16 w-16" />
                                ) : (
                                  <PlayFill className="h-16 w-16" />
                                )}
                              </IconButton>
                              <Typography className="truncate font-medium" variant="body-long-02">
                                {option.label}
                              </Typography>
                            </Stack>
                          )}
                          size="large"
                          value={resolvedVoiceId}
                          onChange={(val: string | { value?: string } | null | undefined) => {
                            const id = getSelectValue(val);
                            if (id) {
                              setLocalVoiceId(id);
                            }
                          }}
                        />
                      )}
                    </Stack>
                  </>
                ) : (
                  <Stack direction="col" gap={12}>
                    <Typography
                      className="font-medium"
                      color="text-text-primary"
                      variant="body-short-01">
                      {t('Course:AiVideoGeneration.Voice')}
                    </Typography>
                    {voicesFetching ? (
                      <Loader size={16} />
                    ) : (
                      <Stack direction="col" gap={12}>
                        {voiceOptions.map((option) => {
                          const isSelected = resolvedVoiceId === option.value;
                          const isPlaying = playingVoiceId === option.value;
                          const createdByName = getCreatedByName(option.voice);

                          return (
                            <Stack
                              key={option.value}
                              alignItems="center"
                              className={cn(
                                'relative cursor-pointer rounded-[12px] border p-12',
                                isSelected ? 'border-text-primary' : 'border-border',
                              )}
                              direction="row"
                              gap={12}
                              onClick={() => setLocalVoiceId(option.value)}>
                              <IconButton
                                appearance="secondary"
                                aria-label={isPlaying ? 'Pause preview' : 'Play preview'}
                                className="h-48 w-48 shrink-0 rounded-full border border-border bg-bg-secondary"
                                disabled={!option.voice?.preview_audio}
                                shape="square"
                                size="xs"
                                variant="ghost"
                                onClick={(e) =>
                                  handleVoicePreviewClick(
                                    e,
                                    option.value,
                                    option.voice?.preview_audio,
                                  )
                                }>
                                {isPlaying ? (
                                  <PauseOutline height={24} width={24} />
                                ) : (
                                  <PlayFill
                                    className={
                                      isSelected ? 'text-text-primary' : 'text-icon-secondary'
                                    }
                                    height={24}
                                    width={24}
                                  />
                                )}
                              </IconButton>
                              <Stack className="min-w-0 flex-1" direction="col" gap={2}>
                                <Typography className="font-medium" variant="body-short-02">
                                  {option.label}
                                </Typography>
                                {createdByName ? (
                                  <Typography color="text-text-secondary" variant="body-short-02">
                                    {t('Course:AiVideoGeneration.CreatedBy', {
                                      name: createdByName,
                                    })}
                                  </Typography>
                                ) : null}
                              </Stack>
                              {isSelected ? (
                                <CheckmarkFill
                                  className="absolute right-12 top-12 text-text-primary"
                                  height={18}
                                  width={18}
                                />
                              ) : null}
                            </Stack>
                          );
                        })}
                      </Stack>
                    )}
                  </Stack>
                )}
              </Stack>
              <Stack
                alignItems="center"
                className="shrink-0 border-t border-border px-20 py-16"
                direction="row"
                gap={12}
                justifyContent="end">
                <Button shape="pill" size="medium" variant="outlined" onClick={handleClose}>
                  {t('Common:Actions.Close')}
                </Button>
                <Button
                  disabled={isApplyDisabled}
                  shape="pill"
                  size="medium"
                  variant="filled"
                  onClick={handleApply}>
                  Apply Changes
                </Button>
              </Stack>
            </>
          )}
        </Popover.Content>
      </Popover>
      {previewAvatar ? (
        <AvatarPreviewModal
          avatar={previewAvatar}
          onChoose={() => {
            handleAvatarSelectedFromGrid(previewAvatar.avatar_id);
            setPreviewAvatarId(null);
          }}
          onClose={() => setPreviewAvatarId(null)}
        />
      ) : null}
    </>
  );
}
